function toScaled(value) {
  const text = String(value ?? '').trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  const [whole, frac = ''] = text.split('.');
  const digits = `${whole}${frac}`.replace(/^0+/, '') || '0';
  return { n: BigInt(digits), scale: frac.length };
}

function formatScaled(n, scale) {
  const text = n.toString().padStart(scale + 1, '0');
  const whole = text.slice(0, text.length - scale);
  const frac = text.slice(text.length - scale);
  return `${whole}.${frac}`;
}

export function moneyPrice(chargePrice, coefficient) {
  const charge = toScaled(chargePrice);
  const coef = toScaled(coefficient);
  if (!charge || !coef || coef.n <= 0n) return null;
  const product = charge.n * coef.n;
  const scale = charge.scale + coef.scale;
  let rounded = product;
  if (scale > 2) {
    const extra = scale - 2;
    const div = 10n ** BigInt(extra);
    const quotient = product / div;
    const remainder = product % div;
    rounded = remainder * 2n >= div ? quotient + 1n : quotient;
  } else if (scale < 2) {
    rounded = product * (10n ** BigInt(2 - scale));
  }
  return formatScaled(rounded, 2);
}

export function formatMoney(value) {
  const normalized = moneyPrice(value, '1');
  if (normalized === null) return '';
  const [whole, frac] = normalized.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${grouped},${frac}`;
}

export function formatSignedMoney(value) {
  const text = String(value ?? '').trim().replace(/\s/g, '').replace(',', '.');
  if (!text) return '';
  const negative = text.startsWith('-');
  const formatted = formatMoney(negative ? text.slice(1) : text);
  if (!formatted) return '';
  return negative ? `-${formatted}` : formatted;
}

export function sumSignedMoney(values) {
  const cents = values.reduce((acc, value) => {
    const text = String(value ?? '').trim().replace(/\s/g, '').replace(',', '.');
    if (!text) return acc;
    const negative = text.startsWith('-');
    const normalized = moneyPrice(negative ? text.slice(1) : text, '1');
    if (normalized === null) return acc;
    const part = BigInt(normalized.replace('.', ''));
    return acc + (negative ? -part : part);
  }, 0n);
  const negative = cents < 0n;
  const abs = (negative ? -cents : cents).toString().padStart(3, '0');
  return `${negative ? '-' : ''}${abs.slice(0, -2)}.${abs.slice(-2)}`;
}

export function formatCoef(value) {
  const text = String(value ?? '').trim().replace(',', '.');
  if (!text) return '';
  const [whole, frac = ''] = text.split('.');
  const trimmed = frac.replace(/0+$/, '');
  return trimmed ? `${whole},${trimmed}` : whole;
}

export function maskCardRequisites(value) {
  if (!value) return '';
  return String(value).replace(/\d(?:[ \d-]{11,22})\d/g, (chunk) => {
    const digits = chunk.replace(/\D/g, '');
    if (digits.length < 13 || digits.length > 19) return chunk;
    return `•••• ${digits.slice(-4)}`;
  });
}

function pad(number) {
  return String(number).padStart(2, '0');
}

export function todayISO(now = new Date()) {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function monthRange(now = new Date()) {
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { dateFrom: todayISO(start), dateTo: todayISO(end) };
}

export function normalizeDecimal(value) {
  return String(value ?? '').trim().replace(/\s/g, '').replace(',', '.');
}

/**
 * Next charge date from charge date + billing period.
 * Keeps the same day-of-month when possible (clamps short months).
 * @returns {string} YYYY-MM-DD or '' for once / unknown
 */
/** Infer LK form currency from saved charge prices. */
export function inferBuyerCurrency(clientPrice, exchangeRate, rubPrice) {
  const rate = normalizeDecimal(exchangeRate);
  const client = normalizeDecimal(clientPrice);
  const rub = normalizeDecimal(rubPrice);
  if (!rate || !client || !rub) return 'usd';
  const rateOne = moneyPrice(rate, '1') === '1.00';
  const same = moneyPrice(client, '1') === moneyPrice(rub, '1');
  return rateOne && same ? 'rub' : 'usd';
}

export function nextChargeDateFromPeriod(chargeDateISO, billingPeriod) {
  const period = String(billingPeriod || '').trim().toLowerCase();
  if (period !== 'month' && period !== 'year') return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(chargeDateISO || '').trim());
  if (!match) return '';
  const year = Number(match[1]);
  const month = Number(match[2]); // 1-12
  const day = Number(match[3]);
  if (!year || !month || !day) return '';

  let nextYear = year;
  let nextMonth = month;
  if (period === 'month') {
    nextMonth += 1;
    if (nextMonth > 12) {
      nextMonth = 1;
      nextYear += 1;
    }
  } else {
    nextYear += 1;
  }
  const lastDay = new Date(nextYear, nextMonth, 0).getDate();
  const nextDay = Math.min(day, lastDay);
  return `${nextYear}-${pad(nextMonth)}-${pad(nextDay)}`;
}
